import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import FeedbackForm from '../../components/FeedbackForm';

// Utility functions (reused from CallsPage)
const formatDuration = (startTime, endTime) => {
  if (!startTime || !endTime) return 'N/A';
  const start = new Date(startTime);
  const end = new Date(endTime);
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) return 'N/A';
  const durationMs = end - start;
  const totalSeconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes === 0 ? `${seconds}s` : `${minutes}m ${seconds}s`;
};

const formatDate = (date) => {
  if (!date) return 'N/A';
  const callDate = new Date(date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  if (callDate >= today) return `Today ${callDate.toLocaleTimeString()}`;
  if (callDate >= yesterday) return `Yesterday ${callDate.toLocaleTimeString()}`;
  return callDate.toLocaleString();
};

const shortenId = (id, length = 8) => {
  if (!id) return 'N/A';
  return id.length <= length + 3 ? id : `${id.substring(0, length)}...`;
};

const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-[200px]">
    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
  </div>
);

const ErrorMessage = ({ message }) => (
  <div className="p-4 md:p-6 2xl:p-10">
    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg" role="alert">
      <strong className="font-semibold">Error:</strong>
      <span className="block sm:inline ml-1">{message}</span>
    </div>
  </div>
);

const AuditDetailsPage = () => {
  const { auditId } = useParams();
  const navigate = useNavigate();
  const [audit, setAudit] = useState(null);
  const [calls, setCalls] = useState([]);
  const [feedbackMap, setFeedbackMap] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCallId, setExpandedCallId] = useState(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);

  useEffect(() => {
    const fetchAuditData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Get the current user for organization access
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError) {
          throw new Error('Authentication error: ' + userError.message);
        }
        if (!user) {
          throw new Error('Not authenticated. Please log in.');
        }

        // Get user's organization
        const { data: memberships, error: membershipsError } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', user.id);

        if (membershipsError) {
          throw new Error('Failed to get organization: ' + membershipsError.message);
        }
        
        if (!memberships || memberships.length === 0) {
          throw new Error('No organization found. Please contact your administrator.');
        }

        const organizationId = memberships[0].organization_id;

        // Fetch audit (exclude soft-deleted)
        const { data: auditData, error: auditError } = await supabase
          .from('audits')
          .select('*')
          .eq('id', auditId)
          .eq('organization_id', organizationId)
          .is('deleted_at', null)
          .single();

        if (auditError) {
          throw new Error('Failed to fetch audit: ' + auditError.message);
        }

        setAudit(auditData);

        // Fetch selected calls for this audit
        const { data: selectedCallsData, error: selectedCallsError } = await supabase
          .from('audit_selected_calls')
          .select('call_id')
          .eq('audit_id', auditId);

        if (selectedCallsError) {
          throw new Error('Failed to fetch selected calls: ' + selectedCallsError.message);
        }

        const callIds = selectedCallsData.map(sc => sc.call_id);

        if (callIds.length === 0) {
          setCalls([]);
          setIsLoading(false);
          return;
        }

        // Fetch full call data
        const { data: callsData, error: callsError } = await supabase
          .from('calls')
          .select(`
            id,
            vapi_call_id,
            assistant_id,
            name,
            status,
            customer_number,
            customer_name,
            customer_email,
            created_at_vapi,
            started_at_vapi,
            ended_at_vapi,
            ended_reason,
            cost,
            cost_breakdown,
            analysis,
            artifact,
            metadata
          `)
          .in('id', callIds)
          .order('created_at_vapi', { ascending: false });

        if (callsError) {
          throw new Error('Failed to fetch calls: ' + callsError.message);
        }

        // Transform calls data
        const transformedCalls = (callsData || []).map(call => ({
          id: call.vapi_call_id,
          dbId: call.id,
          assistantId: call.assistant_id,
          name: call.name,
          status: call.status,
          customer: {
            number: call.customer_number,
            name: call.customer_name,
            email: call.customer_email
          },
          createdAt: call.created_at_vapi,
          startedAt: call.started_at_vapi,
          endedAt: call.ended_at_vapi,
          endedReason: call.ended_reason,
          cost: call.cost,
          costBreakdown: call.cost_breakdown,
          analysis: call.analysis,
          artifact: call.artifact,
          recordingUrl: call.artifact?.recording?.stereoUrl || null,
          ...call.metadata
        }));

        setCalls(transformedCalls);

        // Fetch feedback for all calls
        const { data: feedbackData, error: feedbackError } = await supabase
          .from('audit_call_feedback')
          .select('*')
          .eq('audit_id', auditId);

        if (feedbackError) {
          console.error('Failed to fetch feedback:', feedbackError);
        } else {
          const feedbackMapData = {};
          feedbackData.forEach(fb => {
            const call = transformedCalls.find(c => c.dbId === fb.call_id);
            if (call) {
              feedbackMapData[call.id] = {
                feedback: fb.feedback,
                recordedCorrectlyRating: fb.recorded_correctly_rating,
                pronouncedIntroRating: fb.pronounced_intro_rating,
                reviewedAt: fb.reviewed_at
              };
            }
          });
          setFeedbackMap(feedbackMapData);
        }

        // Check if audit should be marked as completed
        const reviewedCount = feedbackData?.length || 0;
        if (reviewedCount === transformedCalls.length && transformedCalls.length > 0 && auditData.status !== 'completed') {
          // Update audit status to completed
          await supabase
            .from('audits')
            .update({ status: 'completed' })
            .eq('id', auditId);
        }

      } catch (e) {
        console.error("Failed to fetch audit data:", e);
        setError(e.message);
      }
      setIsLoading(false);
    };

    if (auditId) {
      fetchAuditData();
    }
  }, [auditId]);

  const handleFeedbackSave = (feedbackData) => {
    // Find the call ID from the feedback data
    const call = calls.find(c => c.dbId === feedbackData.call_id);
    if (!call) return;

    setFeedbackMap(prev => ({
      ...prev,
      [call.id]: {
        feedback: feedbackData.feedback,
        recordedCorrectlyRating: feedbackData.recorded_correctly_rating,
        pronouncedIntroRating: feedbackData.pronounced_intro_rating,
        reviewedAt: feedbackData.reviewed_at
      }
    }));

    // Check if all calls are reviewed and update audit status
    setTimeout(async () => {
      const { data: allFeedback, error } = await supabase
        .from('audit_call_feedback')
        .select('call_id')
        .eq('audit_id', auditId);

      if (!error && allFeedback) {
        const reviewedCount = allFeedback.length;
        if (reviewedCount === calls.length && calls.length > 0 && audit?.status !== 'completed') {
          await supabase
            .from('audits')
            .update({ status: 'completed' })
            .eq('id', auditId);
          setAudit(prev => prev ? { ...prev, status: 'completed' } : null);
        }
      }
    }, 100);
  };

  const toggleCallExpansion = (callId) => {
    setExpandedCallId(expandedCallId === callId ? null : callId);
  };

  const handleStartEditName = () => {
    setEditedName(audit?.name || '');
    setIsEditingName(true);
  };

  const handleCancelEditName = () => {
    setIsEditingName(false);
    setEditedName('');
  };

  const handleSaveName = async () => {
    if (!audit) return;

    setIsSavingName(true);
    try {
      const { error: updateError } = await supabase
        .from('audits')
        .update({ name: editedName.trim() || null })
        .eq('id', auditId);

      if (updateError) {
        throw updateError;
      }

      // Update local state
      setAudit(prev => prev ? { ...prev, name: editedName.trim() || null } : null);
      setIsEditingName(false);
    } catch (e) {
      console.error('Failed to update audit name:', e);
      alert('Failed to update audit name: ' + e.message);
    } finally {
      setIsSavingName(false);
    }
  };

  const reviewedCount = Object.keys(feedbackMap).filter(id => {
    const fb = feedbackMap[id];
    return fb && (fb.feedback || fb.rating);
  }).length;

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (!audit) return <ErrorMessage message="Audit not found." />;

  return (
    <div className="p-4 md:p-6 2xl:p-10">
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard/audits')}
          className="mb-4 text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-200 flex items-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Audits
        </button>

        <div className="bg-white dark:bg-boxdark shadow-md rounded-lg p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
            <div className="flex items-center gap-3 mb-2 sm:mb-0">
              {isEditingName ? (
                <div className="flex items-center gap-2 flex-1">
                  <input
                    type="text"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    className="text-2xl font-semibold text-gray-800 dark:text-gray-100 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-boxdark-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 flex-1"
                    placeholder="Enter audit name"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSaveName();
                      } else if (e.key === 'Escape') {
                        handleCancelEditName();
                      }
                    }}
                  />
                  <button
                    onClick={handleSaveName}
                    disabled={isSavingName}
                    className="px-3 py-1 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Save"
                  >
                    {isSavingName ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={handleCancelEditName}
                    disabled={isSavingName}
                    className="px-3 py-1 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Cancel"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <>
                  <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">
                    {audit.name || 'Unnamed Audit'}
                  </h1>
                  <button
                    onClick={handleStartEditName}
                    className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded"
                    title="Edit audit name"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                    </svg>
                  </button>
                </>
              )}
            </div>
            <span className={`px-3 py-1 inline-flex text-sm font-semibold rounded-full ${
              audit.status === 'completed' 
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                : audit.status === 'in_progress'
                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                : 'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-200'
            }`}>
              {audit.status === 'completed' ? 'Completed' : audit.status === 'in_progress' ? 'In Progress' : 'Draft'}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Date Range</p>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {new Date(audit.start_date).toLocaleDateString()} - {new Date(audit.end_date).toLocaleDateString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Percentage</p>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{audit.percentage}%</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Calls</p>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{audit.selected_calls_count}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Progress</p>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {reviewedCount} / {calls.length} reviewed
              </p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
              <span>Review Progress</span>
              <span>{calls.length > 0 ? Math.round((reviewedCount / calls.length) * 100) : 0}%</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${calls.length > 0 ? (reviewedCount / calls.length) * 100 : 0}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {calls.length === 0 ? (
        <div className="bg-white dark:bg-boxdark shadow-md rounded-lg p-6">
          <p className="text-gray-700 dark:text-gray-300">No calls selected for this audit.</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-boxdark shadow-md rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Call ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Booking ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Duration</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Phone Number</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Rating</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Feedback Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-boxdark divide-y divide-gray-200 dark:divide-gray-700">
                {calls.map((call) => {
                  const hasFeedback = feedbackMap[call.id] && (
                    feedbackMap[call.id].feedback || 
                    feedbackMap[call.id].recordedCorrectlyRating || 
                    feedbackMap[call.id].pronouncedIntroRating
                  );
                  const recordingAvailable = call.recordingUrl && call.recordingUrl !== 'null' && call.recordingUrl !== 'undefined' && call.recordingUrl !== '';
                  const isExpanded = expandedCallId === call.id;

                  return (
                    <React.Fragment key={call.id}>
                      <tr className="hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {shortenId(call.id)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                          {call.name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                          {formatDate(call.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                          {formatDuration(call.startedAt ?? call.createdAt, call.endedAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                          {call.customer?.number || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                          {call.analysis?.structuredData?.rating !== undefined && call.analysis?.structuredData?.rating !== null
                            ? `${call.analysis.structuredData.rating}`
                            : 'N/R'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {hasFeedback ? (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                              Reviewed
                            </span>
                          ) : (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-200">
                              Not Reviewed
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex space-x-3">
                            {recordingAvailable && (
                              <button
                                onClick={() => {
                                  if (call.recordingUrl && call.recordingUrl !== 'null' && call.recordingUrl !== 'undefined') {
                                    window.open(call.recordingUrl, '_blank');
                                  } else {
                                    alert('Recording URL not available');
                                  }
                                }}
                                className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-200"
                                title="Listen to Recording"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                </svg>
                              </button>
                            )}
                            <button
                              onClick={() => toggleCallExpansion(call.id)}
                              className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
                              title={isExpanded ? "Hide Feedback" : "Show Feedback"}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                {isExpanded ? (
                                  <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                                ) : (
                                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                )}
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan="8" className="px-6 py-4 bg-gray-50 dark:bg-gray-800">
                            <div className="max-w-2xl">
                              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Feedback</h4>
                              <FeedbackForm
                                auditId={auditId}
                                callId={call.dbId}
                                initialFeedback={feedbackMap[call.id]?.feedback}
                                initialRecordedCorrectlyRating={feedbackMap[call.id]?.recordedCorrectlyRating}
                                initialPronouncedIntroRating={feedbackMap[call.id]?.pronouncedIntroRating}
                                onSave={handleFeedbackSave}
                              />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditDetailsPage;

